import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { BusinessUnitBricksService } from '../business-unit-bricks.service';
import { BusinessUnitBricks } from '../../../models/lookups/business-unit-bricks.model';
import { Brick } from '../../../settings/models/brick.model';
import { DisplayModes } from '../../../models/enums';
import { BrickLevel } from '../../../settings/models/brick-level.model';
import { BrickLevelService } from '../../../settings/services/brick-level.service';
import { BricksService } from '../../../settings/services/bricks.service';
import { AlertService, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from '../../../services/app-translation.service';

@Component({
  selector: 'crm-add-business-unit-bricks',
  templateUrl: './add-business-unit-bricks.component.html',
  styleUrls: ['./add-business-unit-bricks.component.css']
})
export class AddBusinessUnitBricksComponent implements OnInit, OnDestroy {

  @Input() businessUnitId: number;
  @Input() isSalesBrick: boolean;
  // the links already in the business unit: bricks with an open link are not offered again
  @Input() existingLinks: BusinessUnitBricks[] = [];
  @Output() closeModalEvent = new EventEmitter<boolean>();

  @ViewChild('brickList') brickList: ElementRef;

  private readonly pageSize = 50;
  // the spinner shows only when a page takes longer than this, so a quick first page never flashes it
  private readonly loadingDelay = 300;
  bricks: Brick[] = [];
  totalBricks = 0;
  private pageNumber = 0;
  loading = false;
  showLoading = false;
  private loadingTimer: any;
  private pageSub: Subscription;
  private searchSub: Subscription;
  private searchChanged = new Subject<string>();
  // the picked bricks by id, kept across pages and searches
  private selected = new Map<number, Brick>();
  selectedBricks: Brick[] = [];
  showSelectedOnly = false;
  brickLevel: BrickLevel = new BrickLevel();
  brickLevels: BrickLevel[] = [];
  // the level fixed by the BusinessUnit(Sales)BricksLevel setting; 0 = not set, any level can be picked
  fixedLevelId = 0;
  search = '';
  displayMode: DisplayModes = DisplayModes.Success;
  startDate: string;
  endDate: string;
  isCentre = false;
  submitted = false;

  constructor(private bricksService: BricksService, private brickLevelService: BrickLevelService,
    private businessUnitBricksService: BusinessUnitBricksService, private alertService: AlertService,
    private translateService: AppTranslationService) {
  }

  ngOnInit() {
    if (typeof (this.isSalesBrick) == 'string')
      this.isSalesBrick = (this.isSalesBrick as any) == 'true';
    this.reset();
    this.searchSub = this.searchChanged.pipe(debounceTime(300)).subscribe(() => this.loadFirstPage());
    this.brickLevelService.getAllBrickLevels().subscribe(response => {
      this.brickLevels = response as BrickLevel[];
      this.businessUnitBricksService.getBricksLevel(this.isSalesBrick).subscribe(levelId => {
        this.fixedLevelId = levelId || 0;
        const fixed = this.brickLevels.find(x => x.id == this.fixedLevelId);
        if (fixed) {
          this.brickLevel = fixed;
          this.onChangeBrickLevel();
        }
      });
    });
  }

  reset() {
    const now = new Date();
    this.startDate = now.getFullYear() + '-' + ('0' + (now.getMonth() + 1)).slice(-2) + '-' + ('0' + now.getDate()).slice(-2);
    this.endDate = '';
    this.isCentre = !this.isSalesBrick && !this.existingLinks.some(x => !x.isClosed);
    this.submitted = false;
    this.search = '';
    this.clearSelection();
  }

  ngOnDestroy() {
    if (this.searchSub) this.searchSub.unsubscribe();
    if (this.pageSub) this.pageSub.unsubscribe();
    clearTimeout(this.loadingTimer);
  }

  get selectedCount(): number {
    return this.selected.size;
  }

  get hasMore(): boolean {
    return this.bricks.length < this.totalBricks;
  }

  onToggle(brick: Brick) {
    if (brick.selected) this.selected.set(brick.id, brick);
    else this.selected.delete(brick.id);
    this.refreshSelected();
  }

  unselect(brick: Brick) {
    this.selected.delete(brick.id);
    brick.selected = false;
    this.bricks.filter(x => x.id == brick.id).forEach(x => x.selected = false);
    this.refreshSelected();
  }

  toggleSelectedOnly() {
    this.showSelectedOnly = !this.showSelectedOnly && this.selectedCount > 0;
    if (this.brickList) this.brickList.nativeElement.scrollTop = 0;
  }

  private refreshSelected() {
    this.selectedBricks = Array.from(this.selected.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    if (this.selectedCount == 0) this.showSelectedOnly = false;
  }

  selectShown() {
    this.bricks.forEach(x => {
      x.selected = true;
      this.selected.set(x.id, x);
    });
    this.refreshSelected();
  }

  clearSelection() {
    this.selected.clear();
    this.bricks.forEach(x => x.selected = false);
    this.refreshSelected();
  }

  clearSearch() {
    this.search = '';
    this.onSearchChange();
  }

  onSearchChange() {
    this.showSelectedOnly = false;
    this.searchChanged.next(this.search);
  }

  onChangeBrickLevel() {
    if (!this.brickLevel || !this.brickLevel.id)
      return;
    this.clearSelection();
    this.loadFirstPage();
  }

  private loadFirstPage() {
    this.bricks = [];
    this.totalBricks = 0;
    this.pageNumber = 0;
    if (this.brickList) this.brickList.nativeElement.scrollTop = 0;
    this.loadNextPage();
  }

  private loadNextPage() {
    if (!this.brickLevel || !this.brickLevel.id)
      return;
    if (this.pageSub) this.pageSub.unsubscribe();
    this.loading = true;
    clearTimeout(this.loadingTimer);
    this.loadingTimer = setTimeout(() => this.showLoading = this.loading, this.loadingDelay);
    const page = this.pageNumber + 1;
    this.pageSub = this.bricksService.searchBricksToLink(this.brickLevel.id, this.businessUnitId, this.isSalesBrick, (this.search || '').trim(), this.pageSize, page)
      .subscribe((response: any) => {
        const items = (response.items || []) as Brick[];
        items.forEach(x => {
          x.selected = this.selected.has(x.id);
          if (x.selected) this.selected.set(x.id, x);
        });
        this.bricks = this.bricks.concat(items);
        this.totalBricks = response.totalItems || 0;
        this.pageNumber = page;
        this.stopLoading();
        // keep loading while the list doesn't fill its box yet, so it can be scrolled
        setTimeout(() => this.onListScroll());
      }, () => {
        this.stopLoading();
        this.alertService.showMessage("", "Failed to load the bricks.", MessageSeverity.Error);
      });
  }

  private stopLoading() {
    this.loading = false;
    this.showLoading = false;
    clearTimeout(this.loadingTimer);
  }

  onListScroll() {
    const el = this.brickList && this.brickList.nativeElement;
    if (!el || this.loading || !this.hasMore || this.showSelectedOnly)
      return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80)
      this.loadNextPage();
  }

  save() {
    this.submitted = true;
    const brickIds = Array.from(this.selected.keys());
    if (!this.startDate || brickIds.length == 0)
      return;
    if (this.endDate && this.endDate < this.startDate) {
      this.alertService.showMessage("", "The end date can't be before the start date.", MessageSeverity.Error);
      return;
    }
    this.displayMode = DisplayModes.Loading;
    this.businessUnitBricksService.addBusinessUnitBricks(this.businessUnitId, brickIds, this.startDate, this.endDate, this.isCentre, this.isSalesBrick)
      .subscribe(() => {
        this.displayMode = DisplayModes.Success;
        this.alertService.showMessage("", this.translateService.getTranslation("Successfully"), MessageSeverity.Success);
        this.closeModalEvent.emit(true);
      }, err => {
        this.displayMode = DisplayModes.Success;
        this.alertService.showMessage("", err && err.error && err.error.Message ? err.error.Message : "Failed to add the bricks.", MessageSeverity.Error);
      });
  }

  cancel() {
    this.closeModalEvent.emit(false);
  }
}
